import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { useConnectedConnectors } from '~/features/onboarding/hooks/use-connections';
import { productClassFields } from '~/features/product-groups/utils/product-groups';
import { useAuthStore } from '~/store/auth.store';
import { extractApiError } from '~/utils/extract-api-error';

import type { ScheduledEnrichmentPlatform } from '../config/scheduled-enrichment-platform';
import { useArchiveEnrichmentJob } from '../hooks/use-archive-enrichment-job';
import type { FullCatalogProgress } from '../hooks/use-full-catalog-progress';
import { useJobRunConfig } from '../hooks/use-job-run-config';
import { useScheduledTriggerStatus } from '../hooks/use-scheduled-trigger-status';
import {
  fetchScheduledJobStatus,
  fetchScheduledSampleEnrichmentQualityScore,
  triggerScheduledContentScoring,
  triggerScheduledPipeline,
  triggerScheduledSampleEnrichment,
} from '../services/scheduled-enrichment.service';
import type { ScheduledEnrichmentStore } from '../store/create-scheduled-enrichment-store';
import type { ExperimentPipelineStage } from '../types/content-scoring.types';
import type { EnrichmentContext, ScheduledFlowData } from '../types/enrichment-context.types';
import type { EnrichmentJobStatus } from '../types/scheduled-enrichment.types';
import { isAnyPhaseLive, toEnrichmentRunConfig } from '../utils/job-run-config';
import { getSubBatchSize } from '../utils/meta-enrichment-config';
import { normalizeRunStatus } from '../utils/normalize-run-status';
import { FUNNEL_STEP, type FunnelStepIndex } from '../utils/phase-funnel-mapping';
import { resolveSampleTotal } from '../utils/sample-progress-total';
import { sampleTriggerPayload } from '../utils/sample-trigger-payload';

/** URL suffix per resumable step. Combined with `{contentEnrichmentBase}/{batchId}`. */
const FUNNEL_STEP_SUFFIX: Partial<Record<FunnelStepIndex, string>> = {
  [FUNNEL_STEP.CURRENT_SCORE]: '',
  [FUNNEL_STEP.REVIEW]: '/review',
  [FUNNEL_STEP.FULL_CATALOG]: '/full',
  [FUNNEL_STEP.OVERVIEW]: '/overview',
  [FUNNEL_STEP.DELIVERY]: '/delivery',
};

/** Lowercase pipeline sub-stage statuses meaning active execution. Mirrors
 *  IN_PROGRESS_STAGE_STATUSES in use-scheduled-batch-resume.ts so the stepper's
 *  canonical step agrees with the step the resume router picked. */
const IN_PROGRESS_PIPELINE_STAGE_STATUSES = new Set([
  'in_progress',
  'running',
  'active',
  'processing',
  'started',
]);

/** Lowercase pipeline sub-stage statuses meaning completion. Mirrors
 *  COMPLETED_STAGE_STATUSES in use-scheduled-batch-resume.ts. */
const COMPLETED_PIPELINE_STAGE_STATUSES = new Set([
  'completed',
  'complete',
  'done',
  'success',
  'succeeded',
]);

/** One pipeline sub-stage as either status source reports it. */
interface RawPipelineStage {
  name: string;
  status: string;
  suppressed?: boolean;
  startedAt?: string | null;
  completedAt?: string | null;
}

/**
 * Normalises a scheduled pipeline stage to the experiment `/pipeline/status` shape the Full
 * Catalog page reads ('Curation' / 'PostScoring', lowercase `in_progress` / `completed`):
 * trigger-status sends Title-case names, the job snapshot UPPER_SNAKE.
 */
function toFullCatalogStage(stage: RawPipelineStage): ExperimentPipelineStage {
  const name = /^[A-Z0-9_]+$/.test(stage.name)
    ? stage.name
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
        .join('')
    : stage.name;
  const raw = stage.status.toLowerCase();
  const status = IN_PROGRESS_PIPELINE_STAGE_STATUSES.has(raw)
    ? 'in_progress'
    : COMPLETED_PIPELINE_STAGE_STATUSES.has(raw)
      ? 'completed'
      : raw;
  return {
    name,
    status,
    ...(stage.startedAt ? { startedAt: stage.startedAt } : {}),
    ...(stage.completedAt ? { completedAt: stage.completedAt } : {}),
    ...(stage.suppressed != null ? { suppressed: stage.suppressed } : {}),
  };
}

/**
 * Returns `status` only when it was recorded against the workflow the funnel is
 * currently watching, otherwise `undefined`.
 *
 * The status fallback chains below mix a live poll with two snapshots — the
 * job-status response and the persisted store — and each snapshot can describe a
 * DIFFERENT (older) workflow than the one being polled: the job-status query is
 * cached with a 30 s staleTime and the backend lags a re-trigger, while the store
 * is per-platform and survives a reload. Letting an unmatched status through is
 * what let a previous run's `FAILED` be reported as the status of a run that is
 * healthily in progress.
 *
 * A `SKIPPED` block is the one exception: the stage started no workflow, so it
 * has no id to match against and `watchedWorkflowId` never resolves — the
 * mismatch guard would otherwise discard `SKIPPED` forever and strand the
 * funnel on this stage. There is nothing for a workflow-less status to be
 * mistaken for, so it's trusted unconditionally.
 */
function statusForWorkflow(
  watchedWorkflowId: string | null,
  sourceWorkflowId: string | null | undefined,
  status: EnrichmentJobStatus | null | undefined,
): EnrichmentJobStatus | undefined {
  if (status === 'SKIPPED' && sourceWorkflowId == null) return status;
  if (!watchedWorkflowId || sourceWorkflowId !== watchedWorkflowId) return undefined;
  return status ?? undefined;
}

/**
 * Builds the {@link EnrichmentContext} for a staged scheduled-enrichment flow
 * (Meta Ads or Google Ads). The flow is platform-agnostic — all differences are
 * supplied via `config`; the store hook is injected so each platform keeps an
 * isolated persisted session.
 *
 * The flow runs three sequential trigger stages inside the shared wizard:
 *   1. Content-scoring  (outer loading screen — gates wizard entry)
 *   2. Sample enrichment (triggered from "Generate Sample Content" on step 0;
 *      polled inline on the Review & Refine page)
 *   3. Full-catalog pipeline (triggered from "Generate Full Catalog" on step 1;
 *      polled inline on the Full Catalog page)
 *
 * `isMetaFlow: true` in `scheduledFlow` tells the layout to treat `bothReady =
 * scoringReady` so the wizard unlocks as soon as content-scoring COMPLETED,
 * without waiting for sample enrichment. `sampleState.hasRun = true` prevents
 * the layout's auto-trigger from firing — sample is always user-driven here.
 * (The flag name is historical; it means "scheduled flow", applies to both platforms.)
 */
export function useScheduledEnrichmentContext(
  config: ScheduledEnrichmentPlatform,
  useStore: ScheduledEnrichmentStore,
): EnrichmentContext {
  const navigate = useNavigate();
  const subsidiaryId = useAuthStore((s) => s.user?.activeTenantId ?? '');

  // batchId from the URL is the authoritative batch identifier. It equals
  // scheduledJobId but is always available from the URL, even when the store
  // hasn't been hydrated (direct URL navigation, stale store, etc.). Read
  // loosely so the same core hook serves both platform route trees.
  const params = useParams({ strict: false });
  const batchId = params.batchId ?? '';

  const {
    connectionId,
    scheduledJobId,
    contentScoringWorkflowId,
    sampleEnrichmentWorkflowId,
    pipelineWorkflowId,
    contentScoringSupersededWorkflowId,
    sampleEnrichmentSupersededWorkflowId,
    pipelineSupersededWorkflowId,
    syncId,
    filters,
    runConfig,
    overrideCache,
    ingestMode,
    carryover,
    productGroups,
    preflightSnapshot,
    contentScoringInitialStatus,
    sampleEnrichmentInitialStatus,
    pipelineInitialStatus,
    setContentScoringWorkflowId,
    setSampleEnrichmentWorkflowId,
    setPipelineWorkflowId,
    setOverrideCache,
    setRunConfig,
    reset: resetStore,
  } = useStore();

  // Fetch the full job-status snapshot on mount. This is the authoritative
  // source for workflow IDs and their current statuses — it makes the funnel
  // self-sufficient regardless of whether the store was hydrated before
  // navigation (e.g. direct URL access, resume from catalog operations).
  // 30 s staleTime avoids re-fetching on every stepper transition.
  const { data: jobStatus } = useQuery({
    queryKey: queryKeys.scheduledEnrichment.jobStatus(batchId),
    queryFn: () => fetchScheduledJobStatus(batchId),
    staleTime: 30_000,
    enabled: !!batchId,
  });

  // Overrides `jobStatus.pipeline` immediately after a (re)trigger. `jobStatus`
  // is only refreshed by a one-shot invalidation that races the backend's
  // eventual consistency; on a retry it already holds the OLD terminal
  // workflow, so the `??` chain below would never pick up the new one without
  // this override, leaving the funnel stuck watching a dead workflow forever.
  const [pipelineRetryOverride, setPipelineRetryOverride] = useState<{
    workflowId: string;
    status: EnrichmentJobStatus;
  } | null>(null);

  // Same race as `pipelineRetryOverride`, for sample enrichment: on a retry,
  // `jobStatus.sampleEnrichment.workflowId` still holds the OLD workflow until
  // the invalidated query resolves, so the `??` chain would keep polling a
  // dead run — the ring shows IN_PROGRESS but totalProducts/succeededProducts
  // never move because the real, progressing workflow is a different ID.
  const [sampleRetryOverride, setSampleRetryOverride] = useState<{
    workflowId: string;
  } | null>(null);

  // Workflow IDs: retry override (freshest, set synchronously on trigger) >
  // job status API (authoritative on this mount) > Zustand store. The store
  // may be stale if the user navigated directly to the URL without going
  // through the resume flow.
  //
  // `snapshotWorkflowId` drops the snapshot's id for a stage when it is the one
  // this browser superseded — the backend echoes the old workflow for a window
  // after a re-trigger, and the in-memory overrides that cover it are lost on
  // reload, which left the funnel polling a dead (usually FAILED) run. The
  // records are scoped to this job; see the store for why this is exact rather
  // than a heuristic.
  const storeOwnsThisJob = scheduledJobId === batchId;
  const snapshotWorkflowId = (
    block: { workflowId?: string } | null | undefined,
    supersededWorkflowId: string | null,
  ): string | null | undefined => {
    if (!block) return undefined;
    if (storeOwnsThisJob && block.workflowId === supersededWorkflowId) return undefined;
    return block.workflowId;
  };

  const effectiveContentScoringWfId =
    snapshotWorkflowId(jobStatus?.contentScoring, contentScoringSupersededWorkflowId) ??
    contentScoringWorkflowId;
  const effectiveSampleWfId =
    sampleRetryOverride?.workflowId ??
    snapshotWorkflowId(jobStatus?.sampleEnrichment, sampleEnrichmentSupersededWorkflowId) ??
    sampleEnrichmentWorkflowId;
  const effectivePipelineWfId =
    pipelineRetryOverride?.workflowId ??
    snapshotWorkflowId(jobStatus?.pipeline, pipelineSupersededWorkflowId) ??
    pipelineWorkflowId;

  // batchId is always a non-null string when this route matches.
  const effectiveJobId = batchId;
  const effectiveSyncId = syncId ?? jobStatus?.syncId ?? null;
  const effectiveFilters = filters ?? jobStatus?.filters ?? null;

  // Resolve the catalog connection for the product-summary endpoint.
  const { data: connectedConnectors } = useConnectedConnectors();
  const catalogConnection = connectedConnectors?.find((c) => c.code === config.connectionCode);
  const resolvedConnectionId = connectionId ?? catalogConnection?.connectionId ?? '';

  // ── Status polling ──────────────────────────────────────────────────────────
  // Polling uses the effective workflow IDs (job status > store) so the hooks
  // start immediately once the job status returns — no re-mount needed.
  const contentScoringStatus = useScheduledTriggerStatus(effectiveContentScoringWfId);
  const sampleStatus = useScheduledTriggerStatus(effectiveSampleWfId);
  const pipelineStatus = useScheduledTriggerStatus(effectivePipelineWfId);

  // SKIPPED is terminal, same as COMPLETED — an operator-disabled stage must not
  // block the quality-score prefetch or the derived `currentPhase` below.
  const scoringCompleted =
    contentScoringStatus.data?.status === 'COMPLETED' ||
    contentScoringStatus.data?.status === 'SKIPPED';
  const sampleCompleted =
    sampleStatus.data?.status === 'COMPLETED' || sampleStatus.data?.status === 'SKIPPED';

  // ── Quality score prefetch ───────────────────────────────────────────────────
  // Prime the quality-score cache as soon as sample enrichment COMPLETED.
  // The page relies on useScheduledSampleEnrichmentQualityScore which uses
  // `enabled` flag gating; that transition is unreliable for deferred one-shot
  // fetches — an explicit prefetch from the polling site is guaranteed.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!sampleCompleted || !effectiveSampleWfId) return;
    void queryClient.prefetchQuery({
      queryKey: queryKeys.scheduledEnrichment.sampleEnrichmentQualityScore(effectiveSampleWfId),
      queryFn: () => fetchScheduledSampleEnrichmentQualityScore(effectiveSampleWfId),
      staleTime: 5 * 60 * 1000,
    });
  }, [sampleCompleted, effectiveSampleWfId, queryClient]);

  // Catalog-ops batch UUID = the enrichment-job id. The job snapshot carries it from the moment
  // the job exists; the trigger polls only once their trigger ran (see `catalogBatchId` below).
  const enrichmentJobId =
    jobStatus?.batchId ??
    contentScoringStatus.data?.batchId ??
    pipelineStatus.data?.batchId ??
    null;

  // The job's stored run config is authoritative: an admin can edit it between runs
  // (`PATCH enrichments/jobs/{id}/config`), and a trigger that restated a stale browser copy
  // would silently revert that edit. So the triggers are built from the server copy, and the
  // persisted store is kept in step with it for every other reader of `runConfig`.
  const { data: storedRunConfig } = useJobRunConfig(enrichmentJobId ?? '');
  const serverRunConfig = storedRunConfig ? toEnrichmentRunConfig(storedRunConfig) : null;
  useEffect(() => {
    if (storedRunConfig && storeOwnsThisJob) setRunConfig(toEnrichmentRunConfig(storedRunConfig));
  }, [storedRunConfig, storeOwnsThisJob, setRunConfig]);

  // Server copy first; the in-browser choice only until it loads (or if the read fails).
  // Null spreads to nothing, so the server default applies rather than an accidental all-off.
  const effectiveRunConfig = serverRunConfig ?? runConfig;
  const runConfigPayload = effectiveRunConfig ?? {};

  // A trigger saves the config it carried onto the job and may record a `*_TRIGGER` history
  // entry, so both reads go stale the moment any trigger succeeds.
  const refreshStoredRunConfig = () => {
    if (!enrichmentJobId) return;
    void queryClient.invalidateQueries({
      queryKey: queryKeys.scheduledEnrichment.jobRunConfig(enrichmentJobId),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.scheduledEnrichment.jobRunConfigHistory(enrichmentJobId),
    });
  };

  // Ingest scope travels with the run configuration for the same reason: a sample
  // enriched under one scope and a full catalog under another are not a preview
  // and its execution. Null — the surface never offered the choice, or the session
  // predates the control — spreads to nothing and leaves the server default.
  const ingestPayload = ingestMode !== null ? { ingestMode } : {};

  // "Not enriched yet" carry-over rides only on the full-catalog trigger: it is the run that
  // enriches them. Off / never offered → the keys are omitted and the server keeps them off.
  const carryoverPayload =
    carryover?.include &&
    carryover.subsidiaryId === subsidiaryId &&
    carryover.jobId === effectiveJobId
      ? {
          includeNotEnriched: true,
          includeFailedPermanent: carryover.includeFailedPermanent,
          includeExcluded: carryover.includeExcluded,
        }
      : {};

  // The product groups the operator included, on the same terms: only the full-catalog trigger,
  // only for the batch and subsidiary they were chosen for. Classes ride only under net_new
  // (an `all` run takes every class); buckets win over the legacy carry-over booleans.
  // An empty class list is sent only for a run that takes earlier batches' products alone: it
  // must say "no sync classes", since an omitted list means the server's default classes. A
  // choice with neither (the stepper never lets one through) also sends [], which workflow
  // rejects as a run that would take nothing, rather than silently falling back to defaults.
  const productGroupsPayload =
    productGroups?.subsidiaryId === subsidiaryId && productGroups.jobId === effectiveJobId
      ? {
          ...productClassFields(productGroups, ingestMode),
          includeNotEnrichedBuckets: productGroups.buckets,
          includeNotEnriched: productGroups.buckets.length > 0,
        }
      : {};

  // ── Content scoring retry ────────────────────────────────────────────────────
  // Fires a fresh content-scoring trigger when the operator retries a FAILED run.
  // Invalidates the job-status cache so `effectiveContentScoringWfId` picks up
  // the new workflowId immediately without waiting for the 30-second TTL.
  const retryContentScoringMutation = useMutation({
    mutationFn: () =>
      triggerScheduledContentScoring({
        connectorCode: config.connectorCode,
        useCase: config.useCase,
        scheduledJobId: effectiveJobId,
        ...(effectiveSyncId ? { syncId: effectiveSyncId } : {}),
        ...(effectiveFilters && Object.keys(effectiveFilters).length > 0
          ? { filters: effectiveFilters }
          : {}),
        subsidiaryId,
        // A retry must reproduce the original run's configuration; without this it
        // silently re-ran with the post-steps off.
        ...runConfigPayload,
        overrideCache,
        ...ingestPayload,
      }),
    onSuccess: (data) => {
      // Never write a null workflowId into the store as if it were a real id —
      // a retry only ever fires from a FAILED status, which content-scoring can't
      // reach when it was skipped, but this stays defensive against that combination.
      if (data.workflowId)
        setContentScoringWorkflowId(data.workflowId, effectiveContentScoringWfId);
      refreshStoredRunConfig();
      void queryClient.invalidateQueries({
        queryKey: queryKeys.scheduledEnrichment.jobStatus(effectiveJobId),
      });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to restart content scoring. Please try again.'));
    },
  });

  // ── Sample enrichment trigger (Current Content Score CTA) ───────────────────
  // Fired when the operator clicks "Generate Sample Content" on the Current
  // Content Score page. The adapter exposes this via `onGenerateSampleContent` so
  // the layout wires it to the footer CTA without knowing about the endpoint.
  const triggerSampleMutation = useMutation({
    // No `contentScoringWorkflowId` guard: content-scoring may have been skipped
    // (`scoring.preScoring = false`), leaving no workflow to send. The field is
    // optional on the wire — the backend resolves the job's scoring pool off
    // `scheduledJobId`, not this id — so it's sent only when one actually exists.
    // Same body Start sends right after create (`sampleTriggerPayload`), so the retry matches it.
    mutationFn: () =>
      triggerScheduledSampleEnrichment(
        sampleTriggerPayload({
          connectorCode: config.connectorCode,
          useCase: config.useCase,
          scheduledJobId: effectiveJobId,
          subsidiaryId,
          syncId: effectiveSyncId,
          filters: effectiveFilters,
          contentScoringWorkflowId: effectiveContentScoringWfId,
          runConfig: runConfigPayload,
          overrideCache,
          ingestMode,
        }),
      ),
    onSuccess: (data) => {
      setSampleEnrichmentWorkflowId(data.workflowId, effectiveSampleWfId);
      setSampleRetryOverride({ workflowId: data.workflowId });
      refreshStoredRunConfig();
      // Invalidate job-status so the new workflowId is reflected immediately
      // rather than waiting for the 30-second stale-time window to expire.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.scheduledEnrichment.jobStatus(effectiveJobId),
      });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to start sample enrichment. Please try again.'));
    },
  });

  // ── Pipeline trigger (step 1 CTA — "Generate Full Catalog") ────────────────
  const triggerPipelineMutation = useMutation({
    // `overrideCache` var lets a retry force a cache bypass without depending on a
    // store update landing before this fires — the closed-over `overrideCache`
    // would otherwise still be the pre-toggle value. Omitted → stored value.
    // No `contentScoringWorkflowId` guard: same reasoning as `triggerSampleMutation`
    // above — content-scoring may have been skipped, and the field is optional.
    mutationFn: (vars?: { overrideCache?: boolean }) =>
      triggerScheduledPipeline({
        connectorCode: config.connectorCode,
        useCase: config.useCase,
        scheduledJobId: effectiveJobId,
        ...(effectiveSyncId ? { syncId: effectiveSyncId } : {}),
        ...(effectiveFilters && Object.keys(effectiveFilters).length > 0
          ? { filters: effectiveFilters }
          : {}),
        ...(effectiveContentScoringWfId
          ? { contentScoringWorkflowId: effectiveContentScoringWfId }
          : {}),
        subsidiaryId,
        batchSize: 50,
        subBatchSize: getSubBatchSize(10),
        // The full-catalog run. This is the one that has to carry the configuration
        // for "all batches" to mean anything — job creation alone only reached the sample.
        ...runConfigPayload,
        overrideCache: vars?.overrideCache ?? overrideCache,
        ...ingestPayload,
        ...carryoverPayload,
        ...productGroupsPayload,
      }),
    onSuccess: (data) => {
      setPipelineWorkflowId(data.workflowId, effectivePipelineWfId);
      // The run claims "not enriched yet" products: the counts other screens cache are now stale.
      void queryClient.invalidateQueries({ queryKey: queryKeys.notEnriched.all });
      setPipelineRetryOverride({ workflowId: data.workflowId, status: data.status });
      refreshStoredRunConfig();
      // Same reason: flush the job-status cache so polling switches to the
      // new pipeline workflowId without a 30-second delay.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.scheduledEnrichment.jobStatus(effectiveJobId),
      });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to start full catalog pipeline. Please try again.'));
    },
  });

  // ── Zero-eligible-products guard: archive this job, then send the operator
  // back to product selection. Unlike experiments, scheduled jobs live in the
  // `enrichment_jobs` table with no stop endpoint — archive is the closest
  // equivalent. `jobId` comes from the content-scoring status response's
  // `batchId` field (the authoritative scheduled-job UUID) rather than the
  // route's `$batchId`, falling back to the route param only if the status
  // poll hasn't resolved yet. Only navigate once the archive succeeds so we
  // never strand the user on a still-running job. The product-selection page
  // resolves its own connection and builds its own scheduledJobId on submit,
  // so a bare store reset (no re-seeding) is sufficient before navigating.
  const archiveJob = useArchiveEnrichmentJob();
  const archiveJobId = contentScoringStatus.data?.batchId ?? batchId;
  const onInvalidateAndRestart = useCallback(() => {
    archiveJob.mutate(
      {
        jobId: archiveJobId,
        payload: { reason: 'No eligible products — restarting from product selection' },
      },
      {
        onSuccess: () => {
          resetStore();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          void navigate({ to: config.productSelectionRoute as any });
        },
      },
    );
  }, [archiveJobId, archiveJob, resetStore, navigate, config.productSelectionRoute]);

  // Ref guard so step 0 and step 1 retries don't double-fire on re-renders.
  const sampleTriggerInFlightRef = useRef(false);

  // ── Navigation ──────────────────────────────────────────────────────────────
  const navigateToStep = useCallback(
    (step: FunnelStepIndex, replace = false) => {
      const suffix = FUNNEL_STEP_SUFFIX[step];
      if (suffix !== undefined && effectiveJobId) {
        const path = `${config.contentEnrichmentBase}/${effectiveJobId}${suffix}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        void navigate({ to: path as any, replace });
      } else {
        void navigate({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          to: '/catalog-operations' as any,
          replace: true,
        });
      }
    },
    [navigate, effectiveJobId, config.contentEnrichmentBase],
  );

  const navigateOnExit = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    void navigate({ to: '/catalog-operations' as any, replace: true });
  }, [navigate]);

  // ── FullCatalogProgress derived from pipeline status ────────────────────────
  // AWAITING_APPROVAL is treated as complete so the full-catalog page auto-
  // advances to the Enrichment Overview after the 3 s hold.
  // Fallback chain: live poll (new workflow) → retry override → job status
  // snapshot → store initial → undefined. The override bridges the gap before
  // the first poll on the new workflowId resolves. Every fallback is scoped to
  // `effectivePipelineWfId` via `statusForWorkflow` — a snapshot describing an
  // earlier pipeline run must read as "not known yet" (keeping the in-progress
  // view up until the live poll answers) rather than as this run's verdict.
  const pipelineJobStatus =
    pipelineStatus.data?.status ??
    statusForWorkflow(
      effectivePipelineWfId,
      pipelineRetryOverride?.workflowId,
      pipelineRetryOverride?.status,
    ) ??
    statusForWorkflow(
      effectivePipelineWfId,
      jobStatus?.pipeline?.workflowId,
      jobStatus?.pipeline?.status,
    ) ??
    statusForWorkflow(effectivePipelineWfId, pipelineWorkflowId, pipelineInitialStatus) ??
    undefined;
  // SKIPPED isn't a state the pipeline reports today, but it's treated as
  // terminal here defensively, same as contentScoring/sampleEnrichment above.
  const isPipelineComplete =
    pipelineJobStatus === 'COMPLETED' ||
    pipelineJobStatus === 'AWAITING_APPROVAL' ||
    pipelineJobStatus === 'SKIPPED';

  // Stage-level view of the pipeline, mirroring resolveTargetStep in
  // use-scheduled-batch-resume.ts so the stepper's canonical step agrees with the
  // URL the resume router navigated to. The overall workflow status alone can't
  // distinguish "enrichment done, delivery pending" from "still enriching" — on an
  // auto-delivery (SCHEDULED_UX_TRIGGER) batch both report a non-terminal status —
  // so we inspect the sub-stages. Prefer the live trigger-status poll (freshest);
  // fall back to the job snapshot the resume seeded. Names matched case-insensitively:
  // trigger-status reports Title-case ('Enrichment'/'Delivery'), job-status UPPER.
  const pipelineStages: { name: string; status: string }[] | null =
    pipelineStatus.data?.stages ?? jobStatus?.pipeline?.stages ?? null;
  const deliveryStageInProgress =
    pipelineStages?.some(
      (s) =>
        s.name.toUpperCase() === 'DELIVERY' &&
        IN_PROGRESS_PIPELINE_STAGE_STATUSES.has(s.status.toLowerCase()),
    ) ?? false;
  const enrichmentStageComplete =
    pipelineStages?.some(
      (s) =>
        s.name.toUpperCase() === 'ENRICHMENT' &&
        COMPLETED_PIPELINE_STAGE_STATUSES.has(s.status.toLowerCase()),
    ) ?? false;
  // Overview is reached once the workflow is terminal/awaiting OR enrichment has
  // finished (delivery may still be pending). Deliberately NOT folded into
  // isPipelineComplete: that predicate drives the full-catalog auto-advance, which
  // must keep gating on the overall status so a live watcher isn't pulled forward
  // mid-delivery.
  const hasReachedOverview = isPipelineComplete || enrichmentStageComplete;

  // Which stage carried the failure, normalized to Title-case for display
  // (job-status stamps UPPER, trigger-status Title-case). Drives the "failed at
  // {stage}" copy. `failureReason` is the raw backend/Temporal string — freshest
  // poll first, job snapshot as fallback — shown only behind the advanced toggle.
  const rawFailedStage =
    pipelineStages?.find((s) => s.status.toLowerCase() === 'failed')?.name ?? null;
  const pipelineFailedStage = rawFailedStage
    ? rawFailedStage.charAt(0).toUpperCase() + rawFailedStage.slice(1).toLowerCase()
    : null;
  const pipelineFailureReason =
    pipelineStatus.data?.failureReason ?? jobStatus?.pipeline?.failureReason ?? null;

  // Batch-config values for the retry dialog — echo first, then in-session config.
  // Kept as consts so the `batchConfig` object below spreads each only when set
  // (exactOptionalPropertyTypes forbids assigning an explicit `undefined`).
  const bcContentAttributes = jobStatus?.contentAttributes ?? effectiveRunConfig?.contentAttributes;
  const bcSearchDescriptors = jobStatus?.searchDescriptors ?? effectiveRunConfig?.searchDescriptors;
  const bcScoring = jobStatus?.scoring ?? effectiveRunConfig?.scoring;

  // The job snapshot's stages only describe this run when its workflow is the watched one.
  const snapshotPipeline = jobStatus?.pipeline;
  const snapshotPipelineStages =
    snapshotPipeline?.workflowId === effectivePipelineWfId ? snapshotPipeline.stages : null;

  const fullCatalogProgress: FullCatalogProgress = {
    isComplete: isPipelineComplete,
    isFailed: pipelineJobStatus === 'FAILED',
    phase: isPipelineComplete ? 'complete' : 'initial',
    processed: isPipelineComplete ? 1 : 0,
    total: 1,
    // Real stages (not `[]`): the page derives the cost card's enrichment phase (actual-spend
    // fetch) and its revisit state from them.
    stages: (pipelineStatus.data?.stages ?? snapshotPipelineStages ?? []).map(toFullCatalogStage),
    // Trigger-status omits `costGate` when nothing is pending, so a loaded response without it
    // reads as null (cleared); the job snapshot's top-level gate covers the first poll when it
    // belongs to the watched workflow. Undefined only while neither has answered.
    costGate: pipelineStatus.data
      ? (pipelineStatus.data.costGate ?? null)
      : jobStatus
        ? jobStatus.costGate?.workflowId === effectivePipelineWfId
          ? jobStatus.costGate
          : null
        : undefined,
    pollingTimedOut: false,
    failedStage: pipelineFailedStage,
    failureReason: pipelineFailureReason,
    isRejected: false,
    error: pipelineStatus.error,
    liveStage: pipelineStatus.data?.liveStage ?? null,
    ...(pipelineStatus.data?.totalProducts != null
      ? { totalProducts: pipelineStatus.data.totalProducts }
      : {}),
    ...(pipelineStatus.data?.succeededProducts != null
      ? { succeededProducts: pipelineStatus.data.succeededProducts }
      : {}),
    startedAt: pipelineStatus.data?.startedAt ?? null,
  };

  // ── ScheduledFlowData ───────────────────────────────────────────────────────
  const scheduledFlow: ScheduledFlowData = {
    // `isMetaFlow: true` signals the layout to gate `bothReady` on content-
    // scoring only (not sample). Sample enrichment is user-triggered from step 0.
    // Applies to every scheduled flow (Meta + Google), not just Meta.
    isMetaFlow: true,
    // Drives the Current Content Score page's summary-field variant (Meta vs GMC).
    platform: config.platform,

    contentScoringState: {
      // Fallback chain: live poll → job status snapshot (authoritative on this
      // page mount) → store initial status (set during resume) → null.
      // This ensures the wizard activates immediately if scoring is already COMPLETED
      // without waiting for the first polling response. Both snapshots are scoped
      // to the watched workflow — see `statusForWorkflow`.
      status:
        contentScoringStatus.data?.status ??
        statusForWorkflow(
          effectiveContentScoringWfId,
          jobStatus?.contentScoring?.workflowId,
          jobStatus?.contentScoring?.status,
        ) ??
        statusForWorkflow(
          effectiveContentScoringWfId,
          contentScoringWorkflowId,
          contentScoringInitialStatus,
        ) ??
        null,
      startedAt: contentScoringStatus.data?.startedAt ?? null,
      // The scheduled content-scoring status endpoint reports live progress via
      // `succeededProducts` (unified stage-count naming), not `scoredProducts` —
      // fall back to it so the progress bar/ETA track real scoring instead of
      // parking at 0%. `scoredProducts` kept first for any legacy response.
      scoredProducts:
        contentScoringStatus.data?.scoredProducts ??
        contentScoringStatus.data?.succeededProducts ??
        0,
      totalProducts: contentScoringStatus.data?.totalProducts ?? null,
      // Live poll first, then the aggregate job snapshot — the latter carries the
      // empty-source FAILED reason on a cold resume before the first poll returns.
      failureReason:
        contentScoringStatus.data?.failureReason ??
        jobStatus?.contentScoring?.failureReason ??
        null,
      pollingTimedOut: false,
      error: contentScoringStatus.error,
      liveStage: contentScoringStatus.data?.liveStage ?? null,
    },

    // `hasRun: true` prevents the layout's auto-trigger effect from firing —
    // sample enrichment is always user-driven in the scheduled flow.
    sampleState: {
      // Same fallback chain: live poll → job status snapshot → store initial → null,
      // each snapshot scoped to the watched workflow (see `statusForWorkflow`).
      status:
        sampleStatus.data?.status ??
        statusForWorkflow(
          effectiveSampleWfId,
          jobStatus?.sampleEnrichment?.workflowId,
          jobStatus?.sampleEnrichment?.status,
        ) ??
        statusForWorkflow(
          effectiveSampleWfId,
          sampleEnrichmentWorkflowId,
          sampleEnrichmentInitialStatus,
        ) ??
        null,
      startedAt: sampleStatus.data?.startedAt ?? null,
      // Live poll first, then the aggregate job snapshot — the latter carries the
      // empty-source FAILED reason on a cold resume before the first poll returns.
      failureReason:
        sampleStatus.data?.failureReason ?? jobStatus?.sampleEnrichment?.failureReason ?? null,
      // Sample-enrichment status responses carry `succeededProducts`, not
      // `scoredProducts` (that field is content-scoring only) — fall back to
      // `scoredProducts` only for older responses that might still send it.
      processed: sampleStatus.data?.succeededProducts ?? sampleStatus.data?.scoredProducts ?? 0,
      // A sample run processes at most `sampleSize` products, and at most as
      // many as the catalog actually holds — so the denominator is whichever is
      // smaller (see `resolveSampleTotal`). Using `sampleSize` alone renders
      // "0 of 10" for an 8-product catalog and never reaches 100%; using
      // `totalProducts` alone renders "n of <full scoring pool>" on large ones.
      // Same live-poll → job-snapshot fallback chain as the other fields.
      total: resolveSampleTotal(
        sampleStatus.data?.totalProducts,
        sampleStatus.data?.sampleSize ?? jobStatus?.sampleEnrichment?.sampleSize,
      ),
      hasRun: true,
      isFetched: sampleStatus.isFetched,
      pollingTimedOut: false,
      error: sampleStatus.error,
      liveStage: sampleStatus.data?.liveStage ?? null,
    },

    // Quality score is fetched directly by the layout and current-content-score-page
    // via useScheduledContentScoringQualityScore — not pre-fetched here.
    contentScoringQualityScore: undefined,

    // No-op: sample enrichment is triggered by onGenerateSampleContent, not auto-triggered.
    onAutoTriggerSample: () => undefined,

    onRetryScoring: () => {
      retryContentScoringMutation.mutate();
    },
    onRetrySample: () => {
      sampleTriggerInFlightRef.current = false;
      triggerSampleMutation.mutate();
    },
    isRetryingScoring: retryContentScoringMutation.isPending,
    isRetryingSample: triggerSampleMutation.isPending,

    sampleQualityScore: null,
    sampleWorkflowId: effectiveSampleWfId,
    fullCatalogProgress,
    isPipelineTriggerPending: triggerPipelineMutation.isPending,
    // Retrying re-triggers the full pipeline run, so bounce the operator back to
    // the Full Catalog Generation screen (step 3) instead of leaving them on a
    // stale Overview page while the new run is in flight.
    // Persist the operator's cache choice (so later triggers in the run reuse it)
    // and pass it explicitly to this trigger to avoid a stale-closure race.
    onRetryPipeline: (overrideCacheOverride?: boolean) => {
      if (overrideCacheOverride !== undefined) setOverrideCache(overrideCacheOverride);
      triggerPipelineMutation.mutate(
        overrideCacheOverride === undefined ? undefined : { overrideCache: overrideCacheOverride },
      );
      navigateToStep(FUNNEL_STEP.FULL_CATALOG, true);
    },
    // Surfaced for the retry dialog: what the run was configured to do, and
    // whether it currently bypasses the enrichment cache.
    runConfig: effectiveRunConfig,
    overrideCache,
    // Pre-flight already-curated breakdown, surfaced to the timeline + overview.
    preflightSnapshot,
    // Full batch config for the retry dialog's "Show full configuration" view.
    // Prefer the job-status echo (authoritative for this batch); fall back to the
    // in-session run config and the flow's own connector/use-case/snapshot. Values
    // are extracted to consts so each spreads only when actually present.
    batchConfig: {
      connectorCode: config.connectorCode,
      useCase: config.useCase,
      syncId: effectiveSyncId,
      ...(bcContentAttributes ? { contentAttributes: bcContentAttributes } : {}),
      ...(jobStatus?.honouredAttributes
        ? { honouredAttributes: jobStatus.honouredAttributes }
        : {}),
      ...(jobStatus?.alwaysOnAttributes
        ? { alwaysOnAttributes: jobStatus.alwaysOnAttributes }
        : {}),
      ...(jobStatus?.enrichedAttributes
        ? { enrichedAttributes: jobStatus.enrichedAttributes }
        : {}),
      ...(jobStatus?.deliveredAttributes
        ? { deliveredAttributes: jobStatus.deliveredAttributes }
        : {}),
      ...(bcSearchDescriptors ? { searchDescriptors: bcSearchDescriptors } : {}),
      ...(bcScoring ? { scoring: bcScoring } : {}),
    },
    pipelineWorkflowId: effectivePipelineWfId ?? null,
    // batchId from the pipeline status endpoint — authoritative after first poll.
    pipelineBatchId: pipelineStatus.data?.batchId ?? null,
    // Catalog-ops batch UUID for `getBatchDetail` reads (Batch Info sheet, Run setup strip).
    // The job-status snapshot carries it from the moment the job exists, so it comes first.
    // The trigger polls only carry it once their trigger ran: with content scoring SKIPPED and
    // no pipeline yet, both were empty and the sheet and strip rendered nothing.
    catalogBatchId: enrichmentJobId,
    // Run-config editing (admin-only, enforced by the panel). Locked while any phase is live —
    // the server 409s then anyway, but a stale poll can disagree in either direction.
    ...(enrichmentJobId
      ? {
          runConfigEdit: {
            jobId: enrichmentJobId,
            isLocked: isAnyPhaseLive([
              contentScoringStatus.data?.status ?? jobStatus?.contentScoring?.status,
              sampleStatus.data?.status ?? jobStatus?.sampleEnrichment?.status,
              pipelineJobStatus,
            ]),
          },
        }
      : {}),
    // The export API keys `enrichment_job` by the scheduled-job ID, so this is
    // what its `sourceWorkflowId` must carry — never `effectivePipelineWfId`.
    scheduledJobId: effectiveJobId || null,
    isDeliverySuppressed:
      pipelineStatus.data?.stages?.some((s) => s.name === 'Delivery' && s.suppressed === true) ??
      false,
  };

  // ── "Generate Sample Content" (Current Content Score CTA) ───────────────────
  // Fires sample enrichment trigger; navigates to Review & Refine on success.
  const onGenerateSampleContent = useCallback(
    (goNext: () => void) => {
      if (sampleTriggerInFlightRef.current || triggerSampleMutation.isPending) return;
      sampleTriggerInFlightRef.current = true;
      triggerSampleMutation.mutate(undefined, {
        onSuccess: () => {
          sampleTriggerInFlightRef.current = false;
          goNext();
        },
        onError: () => {
          sampleTriggerInFlightRef.current = false;
        },
      });
    },
    [triggerSampleMutation],
  );

  // ── "Generate Full Catalog" (Review & Refine CTA) ───────────────────────────
  // Fires pipeline trigger; navigates to Full Catalog on success.
  const onGenerateFullCatalog = useCallback(
    (goNext: () => void) => {
      if (triggerPipelineMutation.isPending) return;
      triggerPipelineMutation.mutate(undefined, { onSuccess: goNext });
    },
    [triggerPipelineMutation],
  );

  // Phase proxy: null disables the layout's funnel guard; URL is source of truth.
  const currentPhase =
    scoringCompleted && sampleCompleted
      ? 'SCHEDULED_SAMPLE_COMPLETE'
      : scoringCompleted
        ? 'SCHEDULED_SCORE_COMPLETE'
        : null;

  // Canonical step derived from job statuses — drives stepper progress marks and
  // clickable-step computation without activating the guard (phaseToStep: () => null).
  // Precedence mirrors resolveTargetStep: DELIVERY in-progress > OVERVIEW (enrichment
  // done / pipeline terminal) > FULL_CATALOG > REVIEW > CURRENT_SCORE, so the stepper's
  // progress marks land on the same step the resume router routed the URL to.
  const canonicalStepIndex: FunnelStepIndex = deliveryStageInProgress
    ? FUNNEL_STEP.DELIVERY
    : hasReachedOverview
      ? FUNNEL_STEP.OVERVIEW
      : effectivePipelineWfId
        ? FUNNEL_STEP.FULL_CATALOG
        : effectiveSampleWfId
          ? FUNNEL_STEP.REVIEW
          : FUNNEL_STEP.CURRENT_SCORE;

  return {
    sessionId: effectiveContentScoringWfId ?? '',
    connectionId: resolvedConnectionId,
    filters: Object.fromEntries(
      Object.entries(effectiveFilters ?? {}).filter(
        (e): e is [string, string | string[]] =>
          e[1] !== null && typeof e[1] !== 'number' && typeof e[1] !== 'boolean',
      ),
    ),
    syncId: effectiveSyncId ?? null,
    currentPhase,
    phaseToStep: () => null,
    canonicalStepIndex,
    postScoringComplete: false,
    navigateToStep,
    navigateOnExit,
    onInvalidateAndRestart,
    isInvalidating: archiveJob.isPending,
    hasSplit: false,
    onGenerateSampleContent,
    isGenerateSampleContentPending: triggerSampleMutation.isPending,
    onGenerateFullCatalog,
    isGenerateFullCatalogPending: triggerPipelineMutation.isPending,
    isTerminalSession: false,
    skuCount:
      contentScoringStatus.data?.totalProducts ?? jobStatus?.contentScoring?.sampleSize ?? 0,
    isRandomFallback: false,
    scheduledFlow,
    // Pipeline-run controls for the shared rail. Reset reuses the existing
    // pipeline trigger (which already supersede-swaps the watched workflowId and
    // bounces the operator to Full Catalog), so the funnel follows the new run.
    workflowControls: {
      workflowId: effectivePipelineWfId ?? null,
      runStatus: normalizeRunStatus(pipelineJobStatus),
      onReset: () => {
        triggerPipelineMutation.mutate(undefined);
        navigateToStep(FUNNEL_STEP.FULL_CATALOG, true);
      },
      isResetting: triggerPipelineMutation.isPending,
      // Scheduled flows run the resumable coordinator, so they get the server-side
      // hard-restart (resume remaining under the same id).
      supportsRestart: true,
      onControlSettled: () => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.scheduledEnrichment.jobStatus(effectiveJobId),
        });
      },
    },
  };
}
